import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/es";

import { DEFAULT_LOCALE, Locale, getIntlLocale } from "lib/i18n/config";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);
dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    s: "now",
    m: "1m",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years",
  },
});
// Mirrors the abbreviated English style above rather than dayjs's Spanish
// defaults ("hace 2 horas"), so the two locales stay visually interchangeable in
// the tight spaces these labels sit in.
dayjs.updateLocale("es", {
  relativeTime: {
    future: "en %s",
    past: "%s",
    s: "ahora",
    m: "1m",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    M: "un mes",
    MM: "%d meses",
    y: "un año",
    yy: "%d años",
  },
});

/**
 * CLDR's abbreviated date-with-year form, the equivalent of ICU's `yMMMd`
 * skeleton. `ll` reads its pattern from each dayjs locale file rather than one
 * written here, so "Jul 19, 2026" and "19 de jul de 2026" both come out right
 * and a third locale needs no pattern work at all.
 */
const ABSOLUTE_FORMAT = "ll";

/**
 * A booked appointment, spelled out: "Sun, Aug 16, 2:30 PM" and
 * "dom, 16 de ago, 2:30 p.m.".
 *
 * The month is a name rather than a number because the numeric order flips --
 * en-US writes 08/16 where es-US writes 16/08, so 08/16/2026 reads as an
 * impossible date to a Spanish reader, or worse, a plausible wrong one. Naming
 * the month removes the ambiguity instead of relying on the reader to know which
 * convention is in play.
 *
 * The weekday is included and the year is not, which is the trade an appointment
 * wants: these are booked days out, so the year carries nothing, while knowing
 * it lands on a Sunday does. The date half matches `weekdayMonthDay` on purpose,
 * so the slot picker and the confirmed appointment read the same.
 */
export const appointmentDateTime = (
  date: Date,
  locale: Locale = DEFAULT_LOCALE,
) =>
  new Intl.DateTimeFormat(getIntlLocale(locale), {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

/**
 * Weekday, month and day -- no year. Built from `Intl` rather than a dayjs
 * pattern on purpose: dayjs only ships a Spain `es` locale, which orders this as
 * "dom., ago 16", while `es-US` gives the "dom, 16 de ago" a reader here expects.
 */
export const weekdayMonthDay = (date: Date, locale: Locale = DEFAULT_LOCALE) =>
  new Intl.DateTimeFormat(getIntlLocale(locale), {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);

/**
 * Time of day. Same reason for avoiding dayjs: its `es` locale is Spain's, so
 * `LT` renders 14:30 where a US Spanish speaker reads 2:30 p.m. `es-US` keeps
 * the 12-hour clock the rest of the site uses.
 */
export const timeOfDay = (date: Date, locale: Locale = DEFAULT_LOCALE) =>
  new Intl.DateTimeFormat(getIntlLocale(locale), {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

/**
 * The absolute form on its own, for dates that are always in the past and read
 * better as a date than as "8 months ago" -- a review's posting date, say.
 *
 * @param dateString Date string
 * @param locale Active locale; defaults to English
 */
export const absoluteDate = (
  dateString: string,
  locale: Locale = DEFAULT_LOCALE,
) => dayjs(dateString).locale(locale).format(ABSOLUTE_FORMAT);

/**
 * Given a date string, returns:
 *   - relative date format if it is within 3 days form now,
 *   - a locale-appropriate absolute format otherwise.
 *
 * The locale is passed in rather than set globally on dayjs: a global would be
 * shared mutable state across concurrent SSR renders, so one request could
 * format another request's dates.
 *
 * @param dateString Date string
 * @param locale Active locale; defaults to English
 */
export const fromNowRecent = (
  dateString: string,
  locale: Locale = DEFAULT_LOCALE,
) => {
  const day = dayjs(dateString).locale(locale);
  if (dayjs().diff(day, "day") <= 3) {
    return day.fromNow();
  }
  return day.format(ABSOLUTE_FORMAT);
};
